import { isString } from "lodash";
import {
  ChangeEvent,
  cloneElement,
  FormEvent,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { CgSpinner } from "react-icons/cg";
import { HiChevronRight } from "react-icons/hi";
import Switch from "@common/components/Switch";
import CoreInput from "@common/components/Input";
import { Typography, Button } from "@earned/core";
import { themeColors } from "@common/constants";

export type InputValue = string | boolean;

interface IInputAccordion {
  label: string;
  disabled?: boolean;
  value?: any;
  displayValue?: any;
  onSubmit?: (
    value: InputValue | FormEvent | MouseEvent,
    key?: string
  ) => Promise<void> | void;
  submitText?: string;
  Input?: ReactElement<any>;
  switchInput?: boolean;
  switchOptions?: any[];
  showButtons?: boolean;
  children?: ReactElement<any>;
  returnEvent?: boolean;
}
const InputAccordion = ({
  label,
  disabled,
  value,
  displayValue,
  submitText = "Save",
  onSubmit,
  Input = <CoreInput />,
  switchInput = false,
  showButtons = true,
  switchOptions = [],
  children,
  returnEvent,
}: IInputAccordion) => {
  const [editedValue, setEditedValue] = useState<InputValue>(value);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const inputRef = useRef<HTMLInputElement>(null);

  const resetError = useCallback(() => {
    error && setError("");
  }, [error, setError]);

  useEffect(() => {
    if (value) setEditedValue(value);
  }, [value]);

  useEffect(() => {
    if (isExpanded) {
      inputRef.current?.setSelectionRange?.(value?.length, value?.length);
      inputRef.current?.focus();
    } else {
      if (!value) {
        setEditedValue("");
      }
      resetError();
    }
  }, [value, isExpanded, resetError]);

  const onClickCancel = useCallback(() => {
    setIsExpanded(false);
  }, [setIsExpanded]);

  const onClickSubmit = useCallback(
    async (event: FormEvent | MouseEvent) => {
      setIsSubmitting(true);
      try {
        await onSubmit?.(returnEvent ? event : editedValue);
        setIsExpanded(false);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (err: any) {
        setError(
          err?.response?.data?.message ||
            "Something went wrong. Please try again."
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [setIsSubmitting, returnEvent, onSubmit, editedValue]
  );

  const onChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement> | InputValue) => {
      resetError();
      if (event.hasOwnProperty("target")) {
        setEditedValue((event as ChangeEvent<HTMLInputElement>).target.value);
      } else {
        setEditedValue(event as InputValue);
      }
    },
    [resetError, setEditedValue]
  );

  const Content = useMemo(() => {
    if (!!children) {
      return children;
    } else if (switchInput) {
      return (
        <div className="flex flex-col gap-1">
          {switchOptions.map((option, index) => (
            <div
              key={index}
              className="-mr-1 flex flex-row items-center justify-between"
            >
              <Typography variant="body" color="primary" weight="medium">
                {option.name}
              </Typography>
              <Switch
                checked={option.value}
                onChange={(checked: boolean) => onSubmit?.(checked, option.key)}
              />
            </div>
          ))}
        </div>
      );
    } else {
      return cloneElement(Input, {
        ref: inputRef,
        onChange,
        value: editedValue,
        /* hack for SelectBox */
        selected: editedValue,
      });
    }
  }, [
    Input,
    children,
    editedValue,
    onChange,
    onSubmit,
    switchInput,
    switchOptions,
  ]);

  return (
    <div
      className={
        "flex flex-col justify-center border-t border-primary py-6 pl-10 pr-9 transition-all last:border-b md:pr-10 " +
        (isExpanded ? "" : "")
      }
    >
      <button
        onClick={() => !disabled && setIsExpanded(!isExpanded)}
        disabled={disabled}
      >
        <div className="flex flex-1 flex-row items-center justify-between">
          <div className="flex h-9 w-full flex-1 flex-row items-center justify-between">
            <div className="flex flex-1">
              <Typography color="primary" weight="medium" variant="body">
                {label}
              </Typography>
            </div>
            <div
              className={
                "transition-opacity " +
                (isExpanded ? "opacity-0" : "opacity-100")
              }
            >
              <Typography variant="body" color="secondary">
                <p className="flex-none truncate">
                  {displayValue ? displayValue : isString(value) ? value : ""}
                </p>
              </Typography>
            </div>
            <div className="flex w-9 flex-none flex-row justify-end">
              {!disabled && (
                <HiChevronRight
                  color={themeColors.green[5]}
                  size={24}
                  className={
                    (isExpanded ? "-rotate-90" : "") +
                    " transition-all duration-300 ease-in-out"
                  }
                />
              )}
            </div>
          </div>
        </div>
      </button>
      <div
        className={"mr-4 flex flex-col py-3 " + (isExpanded ? "" : "hidden")}
      >
        {Content}
        {error && <div className="text-label-base text-red-900">{error}</div>}
        {showButtons && (
          <div className="flex flex-row justify-end gap-2">
            <Button
              variant="secondary"
              onClick={onClickCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={onClickSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <CgSpinner className="animate-spin" size={20} />
              ) : (
                submitText
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default InputAccordion;
